import React from 'react'
import { UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, ComboboxItem, Input } from '@node-space/ui-components'
import { AddBeneficiaryFormErrors, AddBeneficiaryFormPayload } from 'types/beneficiaries'

interface BusinessBeneficiaryFieldsProps {
  availableCurrencies: ComboboxItem[]
  alias: string
  businessName: string
  countries: ComboboxItem[]
  isCountriesLoading: boolean
  countryCode: string
  currencyCode: string
  errors: AddBeneficiaryFormErrors
  register: UseFormRegister<AddBeneficiaryFormPayload>
  setValue: (name: string, value: string) => void
  renderCurrencySpecificFields: () => React.ReactNode
}

export const BusinessBeneficiaryFields = ({
  availableCurrencies,
  alias,
  businessName,
  countries,
  isCountriesLoading,
  countryCode,
  currencyCode,
  errors,
  register,
  setValue,
  renderCurrencySpecificFields,
}: BusinessBeneficiaryFieldsProps) => {
  const { t } = useTranslation()

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(field, event.target.value)
  }

  const handleCountrySelect = (countryCode: string) => {
    const countryName = countries.find(country => country?.value === countryCode)?.leftText
    setValue('countryName', countryName as string)
    setValue('countryCode', countryCode)
  }

  return (
    <Box>
      <Box flex paddingB={16}>
        <Box flexSize="fill">
          <Combobox
            {...register('currencyCode')}
            label={t('manageBeneficiaries.accountCurrency')}
            id="currency-code"
            selectPlaceholder={t('searchOrSelectACurrency')}
            items={availableCurrencies}
            onChange={value => setValue('currencyCode', value)}
            errorText={errors?.currencyCode?.message}
            defaultValue={currencyCode}
          />
        </Box>
      </Box>
      <Box flex paddingB={16}>
        <Box flexSize="fill">
          <Combobox
            {...register('countryCode')}
            data-testid="country-code"
            label={t('manageBeneficiaries.countryOfRegistration')}
            id="country-code"
            selectPlaceholder={t('searchOrSelectACountry')}
            items={countries}
            onChange={handleCountrySelect}
            errorText={errors?.countryCode?.message}
            loading={isCountriesLoading}
            defaultValue={countryCode}
          />
        </Box>
      </Box>

      <Box paddingB={16}>
        <Input
          {...register('businessName')}
          label={t('businessName')}
          placeholder={t('enterBusinessName')}
          inputMode="text"
          name="businessName"
          test-id="business-name"
          errorText={errors?.businessName?.message}
          error={!!errors?.businessName}
          value={businessName}
          onChange={handleInputChange('businessName')}
        />
      </Box>
      <Box paddingB={16}>
        <Input
          {...register('alias')}
          label={t('manageBeneficiaries.beneficiaryDescription')}
          inputMode="text"
          name="alias"
          test-id="alias"
          errorText={errors?.alias?.message}
          error={!!errors?.alias}
          placeholder={t('inputPlaceholders.enterBeneficiaryDescriptionPlaceholder')}
          value={alias}
          onChange={handleInputChange('alias')}
        />
      </Box>
      {!!renderCurrencySpecificFields && <Box>{renderCurrencySpecificFields()}</Box>}
    </Box>
  )
}
